/*
  Falcon — video support for the B2C homepage slider.
  Added by October Communications.

  Sibling of slideshow-video.js, which does the same job on the three
  trade sites. Same behaviour, different carousel: the trade theme runs
  jQuery slick, this one runs Flickity, and their APIs share nothing.

  Why this file exists
  --------------------
  Flickity's autoPlay is one global interval, set to 3 seconds here.
  That is fine for images and wrong for mixed media: a 3s interval
  truncates a 13s video. This turns Flickity's player off and advances
  each slide on its own terms, images on a per-slide timer and videos
  when playback ends.

  It does nothing until a slide has a video URL on it. With no video
  slides the constructor exits before touching the carousel, so the
  homepage behaves exactly as it does today. Remove the script tag from
  sections/homepage-slider.liquid and it reverts either way.
*/
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 738;   // 46.115em, the breakpoint the theme's own <source> tags use
  var FLICKITY_DEFAULT_MS = 3000;
  var SOUND_KEY = 'falcon_slider_sound';

  function isMobile() {
    return window.matchMedia('(max-width: ' + (MOBILE_BREAKPOINT - 1) + 'px)').matches;
  }

  function prefersReducedMotion() {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    return mq && mq.matches;
  }

  function soundOn() {
    try { return sessionStorage.getItem(SOUND_KEY) === '1'; } catch (e) { return false; }
  }
  function setSoundOn(on) {
    try { sessionStorage.setItem(SOUND_KEY, on ? '1' : '0'); } catch (e) {}
  }

  /*
    The slide is an <a> at position:absolute, z-index 5, and its own
    stacking context. Inside it the image is static and the text overlay
    is absolute with z-index auto. A positioned video at z-index 0 lands
    above the static image and, being earlier in the DOM than the text,
    below the text. So the overlay needs no changes, which matters: its
    position carries the alignment settings.
  */
  function injectStyles() {
    if (document.getElementById('falcon-slider-video-css')) return;
    var css = [
      '.c-slider__item .slide__video{position:absolute;top:0;left:0;width:100%;',
      'height:100%;object-fit:cover;z-index:0;pointer-events:none;opacity:0;',
      'transition:opacity .3s ease}',
      /* Revealed only once playback has actually started, so a refused
         autoplay or a slow connection leaves the still image showing
         rather than an empty box. */
      '.c-slider__item--playing .slide__video{opacity:1}',
      '.c-slider__controls{position:absolute;right:24px;bottom:24px;z-index:20;',
      'display:flex;gap:10px;opacity:0;visibility:hidden;',
      'transition:opacity .25s ease,visibility .25s ease}',
      '.c-slider--video-active .c-slider__controls{opacity:1;visibility:visible}',
      '.c-slider__btn{width:44px;height:44px;border-radius:50%;border:0;',
      'cursor:pointer;background:rgba(0,0,0,.45);color:#fff;display:flex;',
      'align-items:center;justify-content:center;padding:0;line-height:0;',
      '-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px)}',
      '.c-slider__btn:hover{background:rgba(0,0,0,.7)}',
      '.c-slider__btn:focus-visible{outline:2px solid #fff;outline-offset:2px}',
      '.c-slider__btn svg{width:20px;height:20px;fill:currentColor}',
      '@media (max-width:' + (MOBILE_BREAKPOINT - 1) + 'px){',
      '.c-slider__controls{right:14px;bottom:58px;gap:8px}',
      '.c-slider__btn{width:40px;height:40px}}'
    ].join('');
    var tag = document.createElement('style');
    tag.id = 'falcon-slider-video-css';
    tag.appendChild(document.createTextNode(css));
    document.head.appendChild(tag);
  }

  var ICON_ON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.8-1-3.3-2.5-4v8c1.5-.7 2.5-2.2 2.5-4zM14 3.2v2.1c2.9.9 5 3.5 5 6.7s-2.1 5.8-5 6.7v2.1c4-1 7-4.5 7-8.8s-3-7.8-7-8.8z"/></svg>';
  var ICON_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm18.6 3l2.1-2.1-1.4-1.4-2.1 2.1-2.1-2.1-1.4 1.4 2.1 2.1-2.1 2.1 1.4 1.4 2.1-2.1 2.1 2.1 1.4-1.4-2.1-2.1z"/></svg>';
  var ICON_REPLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6H4c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8z"/></svg>';

  function Controller(slider, flkty) {
    this.slider = slider;
    this.flkty = flkty;
    this.timer = null;
    this.videos = {};
    this.current = -1;
    this.playsDone = 0;

    /* Read Flickity's own interval before switching it off, and use it as
       the image-slide default. Without this, adding the script would
       silently slow every existing slide from 3s to whatever default the
       schema happens to carry. */
    var a = flkty.options.autoPlay;
    this.nativeMs = (typeof a === 'number' && a > 0) ? a : FLICKITY_DEFAULT_MS;

    injectStyles();
    this.buildVideos();
    if (!Object.keys(this.videos).length) return;   // no video slides: leave the carousel alone

    this.buildControls();
    this.bind();
    this.takeOver();
  }

  Controller.prototype.cells = function () {
    return this.flkty.getCellElements
      ? this.flkty.getCellElements()
      : [].slice.call(this.slider.querySelectorAll('.c-slider__item'));
  };

  Controller.prototype.buildVideos = function () {
    var self = this;
    this.cells().forEach(function (cell, i) {
      if (cell.getAttribute('data-slide-media') !== 'video') return;

      var video = document.createElement('video');
      video.className = 'slide__video';
      /* iOS checks the muted ATTRIBUTE, not only the property, before it
         will allow autoplay. Both are set, before any source is attached. */
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.preload = 'metadata';
      video.loop = false;
      video.setAttribute('aria-hidden', 'true');

      video.dataset.desktopSrc = cell.getAttribute('data-video-desktop') || '';
      video.dataset.mobileSrc = cell.getAttribute('data-video-mobile') || '';
      video.dataset.plays = cell.getAttribute('data-slide-plays') || '1';

      cell.insertBefore(video, cell.firstChild);
      self.videos[i] = video;
    });
  };

  /*
    Poster frame. The theme renders a single <img> per slide inside a
    <picture>, so the browser has already resolved the right breakpoint
    and currentSrc is the correct poster for the current viewport. It is
    lazyloaded, so until lazysizes swaps it in, currentSrc is a base64
    placeholder. Skip those and try again when the image loads.
  */
  Controller.prototype.applyPoster = function (video) {
    var cell = video.parentNode;
    var img = cell ? cell.querySelector('img') : null;
    if (!img) return;
    var src = img.currentSrc || img.getAttribute('src') || '';
    if (!src || src.indexOf('data:') === 0) {
      if (!video.dataset.posterWaiting) {
        video.dataset.posterWaiting = '1';
        var self = this;
        img.addEventListener('load', function () {
          video.dataset.posterWaiting = '';
          self.applyPoster(video);
        }, { once: true });
      }
      return;
    }
    if (video.poster !== src) video.poster = src;
  };

  Controller.prototype.srcFor = function (video) {
    var mobile = video.dataset.mobileSrc;
    return (isMobile() && mobile) ? mobile : video.dataset.desktopSrc;
  };

  Controller.prototype.attach = function (video) {
    if (!video) return;
    this.applyPoster(video);
    var want = this.srcFor(video);
    if (!want) return;
    if (video.getAttribute('src') !== want) {
      video.setAttribute('src', want);
      video.load();
    }
  };

  Controller.prototype.buildControls = function () {
    var self = this;
    var wrap = document.createElement('div');
    wrap.className = 'c-slider__controls';

    var replay = document.createElement('button');
    replay.type = 'button';
    replay.className = 'c-slider__btn';
    replay.innerHTML = ICON_REPLAY;
    replay.setAttribute('aria-label', 'Replay video');
    replay.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.replay();
    });

    var sound = document.createElement('button');
    sound.type = 'button';
    sound.className = 'c-slider__btn';
    sound.innerHTML = soundOn() ? ICON_ON : ICON_OFF;
    sound.setAttribute('aria-label', soundOn() ? 'Mute video' : 'Unmute video');
    sound.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var on = !soundOn();
      setSoundOn(on);
      sound.innerHTML = on ? ICON_ON : ICON_OFF;
      sound.setAttribute('aria-label', on ? 'Mute video' : 'Unmute video');
      self.applySound();
    });

    wrap.appendChild(replay);
    wrap.appendChild(sound);
    this.slider.appendChild(wrap);
  };

  Controller.prototype.applySound = function () {
    var on = soundOn();
    var cur = this.videos[this.current];
    for (var k in this.videos) {
      if (Object.prototype.hasOwnProperty.call(this.videos, k)) this.videos[k].muted = true;
    }
    if (on && cur) cur.muted = false;
  };

  Controller.prototype.clear = function () {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  };

  Controller.prototype.schedule = function (ms) {
    var self = this;
    this.clear();
    this.timer = setTimeout(function () { self.next(); }, ms);
  };

  Controller.prototype.next = function () {
    try { this.flkty.next(true); } catch (e) {}
  };

  /*
    Stop Flickity driving itself. stopPlayer alone is not enough here:
    pauseAutoPlayOnHover is on, so leaving the slider with the mouse can
    put the player back to playing. Clearing the option as well means
    nothing re-arms it behind this controller.
  */
  Controller.prototype.stopNativeAutoplay = function () {
    try { this.flkty.stopPlayer(); } catch (e) {}
    try { this.flkty.options.autoPlay = false; } catch (e) {}
    try { if (this.flkty.player) this.flkty.player.stop(); } catch (e) {}
  };

  Controller.prototype.takeOver = function () {
    this.stopNativeAutoplay();
    this.enter(this.flkty.selectedIndex || 0);
  };

  Controller.prototype.replay = function () {
    var video = this.videos[this.current];
    if (!video) return;
    this.playsDone = 0;
    this.clear();
    try { video.currentTime = 0; } catch (e) {}
    this.enter(this.current);
  };

  Controller.prototype.bind = function () {
    var self = this;

    this.flkty.on('change', function (index) {
      self.stopNativeAutoplay();
      self.enter(index);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.clear();
        var v = self.videos[self.current];
        if (v) { try { v.pause(); } catch (e) {} }
      } else {
        self.enter(self.current, true);
      }
    });

    window.addEventListener('resize', function () {
      var v = self.videos[self.current];
      if (!v) return;
      self.applyPoster(v);
      var want = self.srcFor(v);
      if (want && v.getAttribute('src') !== want) {
        self.attach(v);
        try { v.play(); } catch (e) {}
      }
    });
  };

  /*
    Safety-net length for one play: the clip's own duration plus headroom,
    or a generous wait while the duration is still unknown. Never a
    deadline short enough to punish a buffering video.
  */
  function fallbackFor(video) {
    var d = video.duration;
    return (d && isFinite(d) && d > 0) ? (d * 1000) + 2500 : 20000;
  }

  Controller.prototype.durationFor = function (index) {
    var cell = this.cells()[index];
    var s = cell ? parseInt(cell.getAttribute('data-slide-duration'), 10) : NaN;
    return (isNaN(s) || s < 1) ? this.nativeMs : s * 1000;
  };

  Controller.prototype.enter = function (index, resuming) {
    if (index == null || index < 0) return;
    var self = this;
    this.clear();
    this.current = index;

    for (var k in this.videos) {
      if (!Object.prototype.hasOwnProperty.call(this.videos, k)) continue;
      if (parseInt(k, 10) === index) continue;
      var other = this.videos[k];
      try { other.pause(); other.currentTime = 0; } catch (e) {}
      if (other.parentNode) other.parentNode.classList.remove('c-slider__item--playing');
    }

    var video = this.videos[index];
    this.slider.classList.toggle('c-slider--video-active', !!video);

    if (!video || prefersReducedMotion()) {
      this.schedule(this.durationFor(index));
      return;
    }

    if (!resuming) this.playsDone = 0;

    this.attach(video);
    this.applySound();
    try { if (video.currentTime > 0.05) video.currentTime = 0; } catch (e) {}

    var cell = video.parentNode;
    var wantPlays = parseInt(video.dataset.plays, 10);
    if (isNaN(wantPlays) || wantPlays < 1) wantPlays = 1;

    var cleanup = function () {
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
      video.removeEventListener('loadedmetadata', onMeta);
    };
    var onEnded = function () {
      self.playsDone += 1;
      if (self.playsDone < wantPlays) {
        try {
          video.currentTime = 0;
          var again = video.play();
          if (again && again.catch) again.catch(function () { cleanup(); self.next(); });
          self.schedule(fallbackFor(video));
          return;
        } catch (e) {}
      }
      cleanup();
      self.next();
    };
    var onError = function () { cleanup(); self.schedule(self.durationFor(index)); };
    var onMeta = function () { self.schedule(fallbackFor(video)); };

    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    video.addEventListener('loadedmetadata', onMeta);
    self.schedule(fallbackFor(video));

    var started = video.play();
    if (started && typeof started.then === 'function') {
      started.then(function () {
        if (cell) cell.classList.add('c-slider__item--playing');
      }).catch(function () {
        /* Autoplay refused, usually iOS Low Power Mode. Leave the still
           showing and keep the carousel moving rather than sticking. */
        cleanup();
        if (cell) cell.classList.remove('c-slider__item--playing');
        self.schedule(self.durationFor(index));
      });
    } else if (cell) {
      cell.classList.add('c-slider__item--playing');
    }
  };

  function init() {
    var F = window.Flickity;
    if (!F || !F.data) return false;
    var sliders = [].slice.call(document.querySelectorAll('.js-homepage-slider'));
    if (!sliders.length) return false;
    var any = false;
    sliders.forEach(function (el) {
      if (el.__falconVideo) return;
      var flkty = F.data(el);
      if (!flkty) return;
      el.__falconVideo = new Controller(el, flkty);
      any = true;
    });
    return any;
  }

  /* Flickity initialises from the theme's own bundle, which may run after
     this file. Poll briefly rather than guess at the ordering. */
  function boot() {
    if (init()) return;
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (init() || tries > 40) clearInterval(iv);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
